import { useEffect, useRef, useCallback, useState } from 'react'
import { useTranslations } from 'use-intl'
import { CategoryChips } from './CategoryChips'
import { RestaurantCard } from './RestaurantCard'
import { GlobalSearchBar } from '../search/GlobalSearchBar'
import { BudgetFilter } from '../search/BudgetFilter'
import { AdvancedFilters } from '../search/AdvancedFilters'
import { AreaSelector } from './AreaSelector'

interface ISidebarProps {
  selectedCategory: string
  onSelectCategory: (id: string) => void
  restaurants: any[]
  selectedId: number | null
  onSelectRestaurant: (id: number) => void
  onHover?: (id: number | null) => void
  favoritesOnly?: boolean
  onToggleFavorites?: (val: boolean) => void
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  scrollRef?: React.RefObject<HTMLDivElement | null>
  className?: string
  lat?: number
  lng?: number
  onSelectArea?: (lat: number, lng: number) => void
}

export function Sidebar({
  selectedCategory,
  onSelectCategory,
  restaurants,
  selectedId,
  onSelectRestaurant,
  onHover,
  favoritesOnly,
  onToggleFavorites,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  scrollRef: externalRef,
  className,
  lat,
  lng,
  onSelectArea,
}: Readonly<ISidebarProps>) {
  const t = useTranslations('discovery')
  const internalRef = useRef<HTMLDivElement>(null)
  const scrollRef = externalRef || internalRef

  // Track the observer target element via state so changes trigger re-effects.
  // A callback ref fires synchronously when the conditional div mounts,
  // eliminating the timing race between useRef and useEffect.
  const [observerNode, setObserverNode] = useState<HTMLDivElement | null>(null)
  const observerTargetRef = useCallback((node: HTMLDivElement | null) => {
    setObserverNode(node)
  }, [])

  useEffect(() => {
    if (selectedId && scrollRef.current) {
      const el = document.getElementById(`restaurant-card-${selectedId}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [selectedId])

  // IntersectionObserver: depends on observerNode (state) so it re-creates
  // when the conditional div mounts/unmounts.
  useEffect(() => {
    if (!observerNode) return
    if (!hasNextPage || !fetchNextPage) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { root: scrollRef.current, rootMargin: '400px', threshold: 0 }
    )

    observer.observe(observerNode)

    return () => observer.disconnect()
  }, [observerNode, hasNextPage, isFetchingNextPage, fetchNextPage, scrollRef])

  return (
    <section className={`h-full overflow-y-auto bg-stone-50 dark:bg-stone-900 flex-col p-8 pb-12 border-r border-stone-200 dark:border-stone-800 z-10 ${className ?? 'hidden lg:flex w-[40%]'}`} ref={scrollRef}>
      <div className="max-w-2xl mx-auto w-full">
        <h2 className="text-[2.25rem] font-bold tracking-tight text-stone-900 dark:text-stone-50 mb-8 leading-tight [text-wrap:balance]">
          {t('sidebarTitle')}
        </h2>

        {/* Search & Filters */}
        <div className="flex flex-col gap-4 mb-6">
          <GlobalSearchBar />
          {onSelectArea && (
            <AreaSelector
              onSelect={onSelectArea}
              currentLat={lat}
              currentLng={lng}
            />
          )}
          <BudgetFilter />
          <AdvancedFilters />
        </div>

        {/* Desktop Category Chips */}
        <div className="-mx-4 mb-6">
          <CategoryChips 
            selectedId={selectedCategory} 
            onSelect={onSelectCategory} 
            favoritesOnly={favoritesOnly}
            onToggleFavorites={onToggleFavorites}
          />
        </div>

        {/* Grid of Cards */}
        {restaurants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center mt-4 bg-white dark:bg-stone-800/50 rounded-xl border border-stone-100 dark:border-stone-800 border-dashed">
            <p className="text-stone-500 dark:text-stone-400 font-medium text-sm">
              {t('sidebar.empty', { defaultValue: 'No restaurants in this area. Try panning the map.' })}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 mt-4">
            {restaurants.map(restaurant => (
              <div key={restaurant.id} id={`restaurant-card-${restaurant.id}`}>
                <RestaurantCard
                  restaurant={restaurant}
                  isSelected={String(selectedId) === String(restaurant.id)}
                  onPress={() => onSelectRestaurant(restaurant.id)}
                  onHover={onHover}
                />
              </div>
            ))}
            {hasNextPage && (
              <div ref={observerTargetRef} className="w-full py-4 flex justify-center">
                {isFetchingNextPage ? (
                  <div className="w-6 h-6 border-2 border-stone-300 border-t-primary rounded-full animate-spin"></div>
                ) : (
                  <div className="h-6"></div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
